/**
 * The rich text editor for documents — terms — and one of the two shells
 * TipTap is loaded behind (`note-rich-editor.tsx` is the other, for a task's
 * notes: Aaron, Oct 25, note 1.4).
 *
 * **Loaded on demand.** TipTap and ProseMirror are about 120KB gzipped, on a
 * bundle that is already 260KB, for a screen somebody opens when they write
 * their terms — once a year, if that. So the whole thing sits behind a dynamic
 * import: the Jobs board and the quote builder never pay for it, and it
 * arrives when the terms editor opens. `<RichEditor>` is the shell that does
 * the waiting; `rich-editor-tiptap.tsx` is what it waits for, and nothing else
 * in the app may import that file.
 *
 * **It edits a ProseMirror document, not HTML.** The stored shape is the one
 * `shared/src/terms-doc.ts` defines and sanitises, because terms are rendered
 * twice — as a page and as a PDF — and one structured document renders to both
 * without an HTML parser in the middle. The editor's schema is built to match
 * that allow-list exactly, so what somebody types is what survives the server.
 */
import { Suspense, lazy } from 'react'
import { type TermsDoc } from '@rowted/shared'

const Editor = lazy(() => import('./rich-editor-tiptap.js'))

export interface RichEditorProps {
  value: TermsDoc
  onChange: (doc: TermsDoc) => void
  /** Shown in the empty document, in the voice of what is being written. */
  placeholder?: string
  disabled?: boolean
  /** How tall the writing area is before it grows. */
  minHeight?: number
}

export function RichEditor(props: RichEditorProps) {
  return (
    <Suspense fallback={<EditorSkeleton minHeight={props.minHeight} />}>
      <Editor {...props} />
    </Suspense>
  )
}

/**
 * What the box looks like while the editor is on its way.
 *
 * The same border, the same height and a toolbar-shaped bar, so the panel does
 * not jump when it arrives — a form that reflows the moment you look at it
 * reads as broken however fast it settles.
 */
function EditorSkeleton({ minHeight = 260 }: { minHeight?: number }) {
  return (
    <div className="overflow-hidden rounded-field border border-line bg-surface">
      <div className="flex h-9 items-center gap-1.5 border-b border-line-soft bg-surface-alt px-2">
        {[28, 28, 28, 44, 28, 28].map((width, at) => (
          <span
            key={at}
            style={{ width }}
            className="h-5 rounded-field bg-line-soft"
            aria-hidden
          />
        ))}
      </div>
      <p
        style={{ minHeight }}
        className="m-0 px-3 py-2.5 text-body text-text-faint"
        role="status"
      >
        Loading the editor…
      </p>
    </div>
  )
}
